import { useNavigate } from 'react-router-dom';
import { Wrench, Clock, CheckCircle, AlertCircle, DollarSign, Calendar, MapPin, ChevronRight } from 'lucide-react';
import { useContractorDashboard } from '../../hooks/useDashboard';
import { DashboardSkeleton } from '../../components/design-system/EmptyState';

export default function ContractorDashboard() {
  const navigate = useNavigate();
  const { data: raw, isLoading, isError, error } = useContractorDashboard();

  const jobs = raw?.upcomingJobs ?? [];
  const stats = {
    totalJobs: (raw?.activeJobs ?? 0) + jobs.length,
    activeJobs: raw?.activeJobs ?? 0,
    completedJobs: 0,
    pendingQuotes: raw?.pendingQuotes ?? 0,
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ASSIGNED': return 'bg-blue-100 text-blue-800';
      case 'SCHEDULED': return 'bg-purple-100 text-purple-800';
      case 'IN_PROGRESS': return 'bg-yellow-100 text-yellow-800';
      case 'COMPLETED': return 'bg-green-100 text-green-800';
      case 'QUOTE_PENDING': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency?.toUpperCase()) {
      case 'EMERGENCY': return 'text-red-600';
      case 'HIGH': return 'text-orange-600';
      case 'MEDIUM': return 'text-yellow-600';
      case 'LOW': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'Not scheduled';
    return new Date(dateString).toLocaleDateString('en-AU', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return <DashboardSkeleton />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="text-gray-600 mt-1">Manage your assigned maintenance jobs</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-900">{stats.totalJobs}</p>
            </div>
            <Wrench className="w-10 h-10 text-primary/20" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Active Jobs</p>
              <p className="text-2xl font-bold text-blue-600">{stats.activeJobs}</p>
            </div>
            <Clock className="w-10 h-10 text-blue-100" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Completed</p>
              <p className="text-2xl font-bold text-green-600">{stats.completedJobs}</p>
            </div>
            <CheckCircle className="w-10 h-10 text-green-100" />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Pending Quotes</p>
              <p className="text-2xl font-bold text-orange-600">{stats.pendingQuotes}</p>
            </div>
            <DollarSign className="w-10 h-10 text-orange-100" />
          </div>
        </div>
      </div>

      {/* Jobs List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Your Jobs</h2>
        </div>

        {jobs.length === 0 ? (
          <div className="p-12 text-center">
            <Wrench className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No jobs assigned</h3>
            <p className="text-gray-500">You'll see your assigned maintenance jobs here.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {jobs.map(job => (
              <div
                key={job.id}
                className="p-6 hover:bg-gray-50 cursor-pointer transition-colors"
                onClick={() => navigate(`/contractor/job/${job.id}`)}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(job.status)}`}>
                        {job.status?.replace(/_/g, ' ')}
                      </span>
                      <span className={`text-sm font-medium ${getUrgencyColor(job.urgency)}`}>
                        {job.urgency} Priority
                      </span>
                    </div>

                    <h3 className="font-medium text-gray-900 mb-1">{job.category}</h3>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2">{job.description}</p>

                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span className="flex items-center gap-1">
                        <MapPin className="w-4 h-4" />
                        {job.propertyAddress}
                      </span>
                      {job.scheduledDate && (
                        <span className="flex items-center gap-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(job.scheduledDate)}
                        </span>
                      )}
                      {job.quoteAmount && (
                        <span className="flex items-center gap-1">
                          <DollarSign className="w-4 h-4" />
                          ${job.quoteAmount.toFixed(2)}
                        </span>
                      )}
                    </div>
                  </div>

                  <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {isError && (
        <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{(error as Error)?.message || 'Failed to load dashboard'}</span>
        </div>
      )}
    </div>
  );
}

